// src/pages/events/MyEvents.jsx
// Two roles, one page:
//   - Mentors / superusers see "Events I'm Hosting" (their own events with edit/cancel)
//   - Learners see "Events I'm Attending" (events they've registered for)
// Mentors also see their registrations in a secondary tab.
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Stack, Chip, Button, Tabs, Tab,
  CircularProgress, Avatar, IconButton, Menu, MenuItem,
  useMediaQuery, useTheme, alpha,
} from "@mui/material";
import {
  EventNote, Add, Edit, Block, OpenInNew, People,
  AccessTime, LiveTv, History, EventAvailable, MoreVert,
  CalendarMonth, CheckCircle,
} from "@mui/icons-material";
import axios from "axios";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

const API = "https://backend-global-1vq9.onrender.com";

const C = {
  primary: "#0A2E8A", primaryDark: "#061E5C", primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC", primarySofter: "#F2F5FE",
  secondary: "#10682A", secondaryDark: "#0A4A1C", secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  bg: "#FAFBFC", paper: "#FFFFFF",
  border: "#E5E9F0", borderSoft: "#EEF1F6",
  textPrimary: "#0D1117", textSecondary: "#5B6B7E", textMuted: "#8A9BB0",
  warning: "#D97706", warningSoft: "#FEF3C7",
  error: "#DC2626", errorSoft: "#FEE2E2",
  live: "#DC2626",
};

const PLATFORM_LABELS = {
  "zoom": "Zoom", "google-meet": "Google Meet", "youtube-live": "YouTube Live",
  "microsoft-teams": "Microsoft Teams", "in-person": "In Person", "other": "Other",
};

const PLATFORM_COLORS = {
  "zoom": "#2D8CFF", "google-meet": "#00897B", "youtube-live": "#FF0000",
  "microsoft-teams": "#5059C9", "in-person": "#10682A", "other": "#6B7280",
};

const STATUS_CHIP = {
  upcoming:  { bg: C.primarySoft,   fg: C.primary,    label: "Upcoming" },
  live:      { bg: alpha(C.live, 0.12), fg: C.live,   label: "Live" },
  ended:     { bg: C.borderSoft,    fg: C.textMuted,  label: "Ended" },
  cancelled: { bg: C.errorSoft,     fg: C.error,      label: "Cancelled" },
  draft:     { bg: C.warningSoft,   fg: C.warning,    label: "Draft" },
};

// Tab indices
const TAB_HOSTING     = 0;
const TAB_ATTENDING   = 1;

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const timeUntil = (iso) => {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return "";
  const mins  = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const days  = Math.floor(hours / 24);
  if (days > 0)  return `in ${days}d`;
  if (hours > 0) return `in ${hours}h`;
  return `in ${mins}m`;
};

// Decide how the row's status badge should read
const liveStatus = (event) => {
  if (event.status === "cancelled") return "cancelled";
  if (event.status === "draft")     return "draft";
  if (event.computedStatus)         return event.computedStatus;
  const now = Date.now();
  const start = new Date(event.startTime).getTime();
  const end   = new Date(event.endTime).getTime();
  if (now < start) return "upcoming";
  if (now >= start && now <= end) return "live";
  return "ended";
};

// ── Event row — compact horizontal layout for management views ───────────────
const EventRow = ({ event, mode, onEdit, onCancel, onOpen, onJoin }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const status = liveStatus(event);
  const chip = STATUS_CHIP[status] || STATUS_CHIP.upcoming;
  const isLive = status === "live";
  const isPast = status === "ended";
  const isCancelled = status === "cancelled";
  const platformColor = PLATFORM_COLORS[event.platform] || C.textSecondary;

  // Compact metric — for hosting view show attendee count, for attending show host
  const showMenu = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 }, borderRadius: 3,
        border: `1px solid ${isLive ? alpha(C.live, 0.3) : C.border}`,
        bgcolor: C.paper,
        transition: "all 0.2s ease",
        position: "relative",
        opacity: isCancelled ? 0.7 : 1,
        "&:hover": { borderColor: C.primary, boxShadow: `0 4px 16px ${C.primary}10` },
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
        {/* Date block — left rail */}
        <Box sx={{
          width: { xs: "100%", sm: 76 }, flexShrink: 0,
          textAlign: { xs: "left", sm: "center" },
          py: { xs: 0, sm: 0.5 },
          px: { xs: 0, sm: 1 },
          borderRight: { sm: `1px solid ${C.borderSoft}` },
        }}>
          <Typography sx={{
            fontSize: 10, fontWeight: 800, letterSpacing: 1,
            textTransform: "uppercase", color: C.primary, mb: 0.25,
          }}>
            {new Date(event.startTime).toLocaleDateString(undefined, { month: "short" })}
          </Typography>
          <Typography sx={{
            fontWeight: 800, fontSize: 22, color: C.textPrimary, lineHeight: 1,
            mb: 0.25,
          }}>
            {new Date(event.startTime).getDate()}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: C.textMuted, fontWeight: 600 }}>
            {new Date(event.startTime).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
          </Typography>
        </Box>

        {/* Main content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              label={chip.label}
              size="small"
              icon={
                isLive ? <Box sx={{
                  width: 6, height: 6, borderRadius: "50%", bgcolor: C.live, ml: "5px !important",
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.4 },
                  },
                }} /> : null
              }
              sx={{
                bgcolor: chip.bg, color: chip.fg,
                fontWeight: 800, fontSize: 10, height: 20,
                "& .MuiChip-label": { px: 0.85 },
              }}
            />
            <Chip
              label={PLATFORM_LABELS[event.platform] || event.platform}
              size="small"
              sx={{
                bgcolor: alpha(platformColor, 0.1), color: platformColor,
                fontWeight: 700, fontSize: 10, height: 20,
                "& .MuiChip-label": { px: 0.85 },
              }}
            />
            {!isPast && !isCancelled && (
              <Typography sx={{ fontSize: 11, color: C.textMuted, fontWeight: 600, ml: "auto !important" }}>
                {timeUntil(event.startTime)}
              </Typography>
            )}
          </Stack>

          <Typography
            onClick={() => onOpen(event)}
            sx={{
              fontWeight: 800, fontSize: 15.5, color: C.textPrimary,
              lineHeight: 1.3, mb: 0.5, letterSpacing: -0.2,
              cursor: "pointer",
              overflow: "hidden", display: "-webkit-box",
              WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
              "&:hover": { color: C.primary },
            }}
          >
            {event.title}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AccessTime sx={{ fontSize: 12, color: C.textMuted }} />
              <Typography sx={{ fontSize: 11.5, color: C.textSecondary, fontWeight: 600 }}>
                {formatDateTime(event.startTime)}
              </Typography>
            </Stack>

            {mode === "hosting" ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <People sx={{ fontSize: 12, color: C.textMuted }} />
                <Typography sx={{ fontSize: 11.5, color: C.textSecondary, fontWeight: 600 }}>
                  {event.attendees?.length || 0}
                  {event.capacity > 0 ? ` / ${event.capacity}` : ""} registered
                </Typography>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography sx={{ fontSize: 11.5, color: C.textSecondary, fontWeight: 600 }}>
                  hosted by {event.hostName || "—"}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Action rail */}
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          {/* Primary action depends on mode + state */}
          {mode === "attending" && isLive && (
            <Button
              variant="contained"
              startIcon={<LiveTv sx={{ fontSize: 16 }} />}
              onClick={() => onJoin(event)}
              sx={{
                bgcolor: C.live, color: "#fff", fontWeight: 800, fontSize: 12,
                textTransform: "none", borderRadius: 2.5, px: 1.75, py: 0.75,
                boxShadow: `0 4px 12px ${C.live}40`,
                "&:hover": { bgcolor: C.live, opacity: 0.92 },
              }}
            >
              Join
            </Button>
          )}
          {mode === "attending" && !isLive && !isPast && !isCancelled && (
            <Button
              onClick={() => onOpen(event)}
              startIcon={<EventAvailable sx={{ fontSize: 16 }} />}
              sx={{
                color: C.primary, bgcolor: C.primarySoft, fontWeight: 700, fontSize: 12,
                textTransform: "none", borderRadius: 2.5, px: 1.5, py: 0.75,
                "&:hover": { bgcolor: C.primarySofter },
              }}
            >
              View
            </Button>
          )}

          {mode === "hosting" && !isCancelled && !isPast && (
            <Button
              onClick={() => onEdit(event)}
              startIcon={<Edit sx={{ fontSize: 16 }} />}
              sx={{
                color: C.primary, bgcolor: C.primarySoft, fontWeight: 700, fontSize: 12,
                textTransform: "none", borderRadius: 2.5, px: 1.5, py: 0.75,
                "&:hover": { bgcolor: C.primarySofter },
              }}
            >
              Edit
            </Button>
          )}

          <IconButton size="small" onClick={showMenu} sx={{ color: C.textMuted }}>
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            PaperProps={{ sx: { borderRadius: 2.5, border: `1px solid ${C.border}`, mt: 1 } }}
          >
            <MenuItem onClick={() => { closeMenu(); onOpen(event); }} sx={{ fontSize: 13 }}>
              <OpenInNew sx={{ fontSize: 16, mr: 1, color: C.textSecondary }} />
              View details
            </MenuItem>
            {mode === "hosting" && !isCancelled && !isPast && (
              <>
                <MenuItem onClick={() => { closeMenu(); onEdit(event); }} sx={{ fontSize: 13 }}>
                  <Edit sx={{ fontSize: 16, mr: 1, color: C.textSecondary }} />
                  Edit
                </MenuItem>
                <MenuItem onClick={() => { closeMenu(); onCancel(event); }} sx={{ fontSize: 13, color: C.error }}>
                  <Block sx={{ fontSize: 16, mr: 1 }} />
                  Cancel event
                </MenuItem>
              </>
            )}
          </Menu>
        </Stack>
      </Stack>
    </Paper>
  );
};

// ── Empty state component ────────────────────────────────────────────────────
const EmptyState = ({ mode, onCreate, canHost }) => (
  <Paper elevation={0} sx={{
    p: { xs: 4, sm: 6 }, textAlign: "center", borderRadius: 3,
    border: `1px solid ${C.border}`, bgcolor: C.paper,
  }}>
    <Box sx={{
      width: 80, height: 80, borderRadius: "50%",
      background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
      display: "flex", alignItems: "center", justifyContent: "center",
      mx: "auto", mb: 2,
      boxShadow: `0 8px 20px ${C.primary}30`,
    }}>
      {mode === "hosting"
        ? <CalendarMonth sx={{ fontSize: 36, color: "#fff" }} />
        : <EventAvailable sx={{ fontSize: 36, color: "#fff" }} />}
    </Box>
    <Typography sx={{ fontWeight: 800, color: C.textPrimary, mb: 0.75, fontSize: 16 }}>
      {mode === "hosting" ? "No events yet" : "You haven't registered for any events"}
    </Typography>
    <Typography sx={{ color: C.textSecondary, fontSize: 13, mb: 3, maxWidth: 380, mx: "auto" }}>
      {mode === "hosting"
        ? "Workshops, AMAs, networking sessions — host your first event and bring the community together."
        : "Browse upcoming events on the platform and register for ones that interest you."}
    </Typography>
    {mode === "hosting" && canHost ? (
      <Button
        startIcon={<Add />}
        onClick={onCreate}
        variant="contained"
        sx={{
          textTransform: "none", fontWeight: 800, fontSize: 14,
          background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
          color: "#fff", px: 3, py: 1.25, borderRadius: 2.5,
          boxShadow: `0 4px 14px ${C.primary}30`,
          "&:hover": { boxShadow: `0 6px 18px ${C.primary}45` },
        }}
      >
        Create Your First Event
      </Button>
    ) : mode === "attending" ? (
      <Button
        startIcon={<EventNote />}
        onClick={onCreate} // reused as "browse" callback
        variant="contained"
        sx={{
          textTransform: "none", fontWeight: 800, fontSize: 14,
          background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
          color: "#fff", px: 3, py: 1.25, borderRadius: 2.5,
          boxShadow: `0 4px 14px ${C.primary}30`,
          "&:hover": { boxShadow: `0 6px 18px ${C.primary}45` },
        }}
      >
        Browse Events
      </Button>
    ) : null}
  </Paper>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function MyEvents() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  })();
  const userId = user?.id;
  const userRole = (user?.role || "").toLowerCase();
  const canHost = userRole === "mentor" || userRole === "superuser";

  // Tab default: hosting if mentor, attending if learner
  const [tabValue, setTabValue] = useState(canHost ? TAB_HOSTING : TAB_ATTENDING);

  const [hostedEvents, setHostedEvents] = useState([]);
  const [attendingEvents, setAttendingEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Fetch both lists in parallel ──
  const fetchAll = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const promises = [];

      // Hosted: filter by hostId, status=all so we can show drafts/cancelled too
      if (canHost) {
        promises.push(
          axios.get(`${API}/api/events`, {
            params: { hostId: userId, status: "all", limit: 50 },
          })
        );
      } else {
        promises.push(Promise.resolve({ data: { data: [] } }));
      }

      // Attending: dedicated endpoint
      promises.push(axios.get(`${API}/api/events/my-registrations/${userId}`));

      const [hostedRes, attendingRes] = await Promise.all(promises);
      setHostedEvents(hostedRes.data?.data || []);
      setAttendingEvents(attendingRes.data?.data || []);
    } catch (err) {
      console.error("Failed to load my events:", err);
    } finally {
      setLoading(false);
    }
  }, [userId, canHost]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Actions ──
  const openEvent  = (ev) => navigate(`/events/${ev._id}`);
  const editEvent  = (ev) => navigate(`/events/${ev._id}/edit`);
  const joinEvent  = (ev) => { if (ev.meetingUrl) window.open(ev.meetingUrl, "_blank", "noopener,noreferrer"); };

  const cancelEvent = async (ev) => {
    if (!window.confirm(`Cancel "${ev.title}"? Attendees won't be able to join.`)) return;
    try {
      await axios.post(`${API}/api/events/${ev._id}/cancel`, {
        requesterId: userId, requesterRole: userRole,
      });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to cancel event");
    }
  };

  // ── Computed stat tiles for mentors ──
  const stats = {
    total: hostedEvents.length,
    upcoming: hostedEvents.filter((e) => liveStatus(e) === "upcoming").length,
    live: hostedEvents.filter((e) => liveStatus(e) === "live").length,
    totalAttendees: hostedEvents.reduce((acc, e) => acc + (e.attendees?.length || 0), 0),
  };

  // ── Active list based on tab ──
  const activeList = (tabValue === TAB_HOSTING ? hostedEvents : attendingEvents);

  // Sort: live first, then upcoming by date, then past
  const sortedList = [...activeList].sort((a, b) => {
    const sa = liveStatus(a), sb = liveStatus(b);
    const order = { live: 0, upcoming: 1, ended: 2, cancelled: 3, draft: 4 };
    if (order[sa] !== order[sb]) return order[sa] - order[sb];
    // Within the same status — newest first for ended, soonest first for upcoming
    if (sa === "ended" || sa === "cancelled") {
      return new Date(b.startTime) - new Date(a.startTime);
    }
    return new Date(a.startTime) - new Date(b.startTime);
  });

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, md: 4 }, py: 3, maxWidth: 1200, mx: "auto", width: "100%" }}>
        {/* Hero header */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 },
            mb: 3,
            color: "#fff",
            boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute",
            top: "-40%", right: "-10%",
            width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
                  <EventNote sx={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }} />
                  <Typography sx={{
                    fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                    textTransform: "uppercase", color: "rgba(255,255,255,0.75)",
                  }}>
                    Personal Calendar
                  </Typography>
                </Stack>
                <Typography sx={{
                  fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
                  color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
                }}>
                  My Events
                </Typography>
                <Typography sx={{
                  color: "rgba(255,255,255,0.85)",
                  fontSize: { xs: 13, sm: 14 }, maxWidth: 560, lineHeight: 1.5,
                }}>
                  {canHost
                    ? "Manage events you're hosting and ones you've registered for"
                    : "Events you've registered for, in one place"}
                </Typography>
              </Box>

              {canHost && (
                <Button
                  startIcon={<Add />}
                  onClick={() => navigate("/events/new")}
                  sx={{
                    bgcolor: "#fff", color: C.primary,
                    fontWeight: 800, fontSize: 13,
                    textTransform: "none", borderRadius: 2.5,
                    px: 2.5, py: 1, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    flexShrink: 0,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.92)", boxShadow: "0 6px 16px rgba(0,0,0,0.2)" },
                  }}
                >
                  Create Event
                </Button>
              )}
            </Stack>
          </Box>
        </Paper>

        {/* Stat tiles — only for mentors */}
        {canHost && (
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
            gap: 2, mb: 3,
          }}>
            {[
              { label: "Total events", value: stats.total, icon: <EventNote />, color: C.primary },
              { label: "Upcoming", value: stats.upcoming, icon: <AccessTime />, color: C.primary },
              { label: "Live now", value: stats.live, icon: <LiveTv />, color: C.live },
              { label: "Registrations", value: stats.totalAttendees, icon: <People />, color: C.secondary },
            ].map((tile) => (
              <Paper key={tile.label} elevation={0} sx={{
                p: 2, borderRadius: 3,
                border: `1px solid ${C.border}`, bgcolor: C.paper,
                boxShadow: "0 2px 8px rgba(13,17,23,0.03)",
              }}>
                <Stack direction="row" alignItems="center" spacing={1.25} mb={0.75}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: 1.5,
                    bgcolor: alpha(tile.color, 0.12),
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {React.cloneElement(tile.icon, { sx: { fontSize: 16, color: tile.color } })}
                  </Box>
                  <Typography sx={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: 0.8,
                    textTransform: "uppercase", color: C.textMuted,
                  }}>
                    {tile.label}
                  </Typography>
                </Stack>
                <Typography sx={{ fontWeight: 800, fontSize: 24, color: C.textPrimary, lineHeight: 1, letterSpacing: -0.5 }}>
                  {tile.value}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {/* Tabs — only visible if mentor (learners just see attending list directly) */}
        {canHost && (
          <Paper elevation={0} sx={{
            mb: 2.5, borderRadius: 3,
            border: `1px solid ${C.border}`, bgcolor: C.paper,
            boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
          }}>
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              variant={isMobile ? "fullWidth" : "standard"}
              sx={{
                px: { xs: 0, sm: 1.5 }, minHeight: 48,
                "& .MuiTab-root": {
                  textTransform: "none", fontWeight: 700, fontSize: 13.5,
                  minHeight: 48, color: C.textSecondary, px: 2,
                },
                "& .MuiTabs-indicator": { bgcolor: C.primary, height: 3 },
                "& .Mui-selected": { color: `${C.primary} !important` },
              }}
            >
              <Tab
                icon={<CalendarMonth sx={{ fontSize: 16 }} />}
                iconPosition="start"
                label={`Hosting (${hostedEvents.length})`}
              />
              <Tab
                icon={<EventAvailable sx={{ fontSize: 16 }} />}
                iconPosition="start"
                label={`Attending (${attendingEvents.length})`}
              />
            </Tabs>
          </Paper>
        )}

        {/* Event list */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress sx={{ color: C.primary }} />
          </Box>
        ) : sortedList.length === 0 ? (
          <EmptyState
            mode={tabValue === TAB_HOSTING ? "hosting" : "attending"}
            canHost={canHost}
            onCreate={() => navigate(tabValue === TAB_HOSTING ? "/events/new" : "/events")}
          />
        ) : (
          <Stack spacing={1.5}>
            {sortedList.map((ev) => (
              <EventRow
                key={ev._id}
                event={ev}
                mode={tabValue === TAB_HOSTING ? "hosting" : "attending"}
                onOpen={openEvent}
                onEdit={editEvent}
                onCancel={cancelEvent}
                onJoin={joinEvent}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Footer />
    </Box>
  );
}